import { Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { SubscriptionPlansService } from '../../services/subscription-plans.service';
import { SubscriptionPlan } from '../../models/subscription-plan.model';
import { AccountContextService } from '../../services/account-context.service';
import { SubscriptionService } from '../../services/subscription.service';
import { FirestoreService } from '../../services/firestore.service';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-upgrade-subscription',
  templateUrl: './upgrade-subscription.component.html',
  styleUrl: './upgrade-subscription.component.css'
})
export class UpgradeSubscriptionComponent {
  userId: string = '';
  isSubscriptionActive: boolean = false;
  subscriptionLevel: string = '';
  subscriptionName: string = '';
  userData: any = null;
  expiresAt: Date | null = null;
  maxBoatsAllowed: number | null = null;
  latestSubscriptionId: string | null = null;
  showPlanSelectorForInactive = false;
  readonly plans = toSignal(this.subscriptionPlansService.getPublishedPlansWithFallback(), { initialValue: [] as SubscriptionPlan[] });
  readonly recommendedPlan = computed(() => {
    const current = this.getPlanByLevel(this.subscriptionLevel);
    return this.plans().find(plan => plan.powerOrder === (current?.powerOrder ?? 0) + 1) ?? null;
  });

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly subscriptionPlansService: SubscriptionPlansService,
    private readonly accountContextService: AccountContextService,
    private readonly subscriptionService: SubscriptionService,
    private readonly firestoreService: FirestoreService
  ) {}

  ngOnInit(): void {

    this.authService.user$.subscribe(user => {
      if (user) {
        this.userId = user.uid;
      }
    });

    this.accountContextService.activeSubscriptionProfile$.subscribe(subscriptionProfile => {
      if (!subscriptionProfile) {
        this.redirectToSubscriptions();
        return;
      }
      this.isSubscriptionActive = subscriptionProfile.isSubscriptionActive || false;
      this.subscriptionLevel = subscriptionProfile.subscriptionLevel;
      this.subscriptionName = subscriptionProfile.subscriptionName;
      this.expiresAt = subscriptionProfile.expiresAt;
      this.maxBoatsAllowed = subscriptionProfile.maxBoatsAllowed ?? this.getPlanByLevel(this.subscriptionLevel)?.maxBoatsAllowed ?? null;
      this.latestSubscriptionId = subscriptionProfile.latestSubscriptionId ?? null;
      if (!this.isSubscriptionActive && !this.subscriptionLevel) {
        this.redirectToSubscriptions();
      }
    });
  }

  getPlanByLevel(level: string) {
    return this.plans().find(plan => plan.level === level);
  }

  get currentBoatLimitLabel(): string {
    if (typeof this.maxBoatsAllowed === 'number' && Number.isFinite(this.maxBoatsAllowed)) {
      return String(this.maxBoatsAllowed);
    }

    const currentPlanLimit = this.getPlanByLevel(this.subscriptionLevel)?.maxBoatsAllowed;
    if (typeof currentPlanLimit === 'number' && Number.isFinite(currentPlanLimit)) {
      return String(currentPlanLimit);
    }

    return 'your current number of';
  }


  canChoosePlan(plan: SubscriptionPlan): boolean {
    if (plan.level === this.subscriptionLevel) {
      return false;
    }

    const currentPlan = this.getPlanByLevel(this.subscriptionLevel);
    const currentPowerOrder = currentPlan?.powerOrder;
    const planPowerOrder = plan.powerOrder;

    const hasCurrentOrder = typeof currentPowerOrder === 'number' && currentPowerOrder > 0;
    const hasTargetOrder = typeof planPowerOrder === 'number' && planPowerOrder > 0;

    if (hasCurrentOrder && hasTargetOrder) {
      return planPowerOrder > currentPowerOrder;
    }

    return true;
  }

  choosePlan(plan: SubscriptionPlan): void {
    if (!this.canChoosePlan(plan)) {
      return;
    }

    this.subscriptionService.setSelectedPlan(plan.level);
    this.createUpgradeSubscriptionAndNavigate(plan.level, true);
  }

  renewPlan(): void {
    if (!this.subscriptionLevel) {
      return;
    }

    this.subscriptionService.setSelectedPlan(this.subscriptionLevel);
    this.createUpgradeSubscriptionAndNavigate(this.subscriptionLevel, false);
  }

  private createUpgradeSubscriptionAndNavigate(planLevel: string, isUpgrade: boolean): void {
    if (!this.userId) {
      this.router.navigate(['/checkout'], {
        state: {
          selectedPlanLevel: planLevel,
          existingSubscriptionId: this.latestSubscriptionId,
          previousSubscriptionId: this.latestSubscriptionId,
          isUpgrade
        }
      });
      return;
    }

    const newSubscriptionPayload = {
      userId: this.userId,
      planId: planLevel,
      status: isUpgrade ? 'upgrading' : 'renewing',
      paymentId: null,
      previousSubscriptionId: this.latestSubscriptionId,
      error: null,
      createdAt: '',
      updatedAt: ''
    };

    this.firestoreService.recordPlanSelection(newSubscriptionPayload).pipe(take(1)).subscribe({
      next: (newSubscriptionId) => {
        const previousSubscriptionId = this.latestSubscriptionId;

        const tasks: Promise<unknown>[] = [];

        tasks.push(
          firstValueFrom(this.firestoreService.updateUserSubscriptionId(this.userId, newSubscriptionId))
            .catch((err) => console.error('Failed to update user latestSubscriptionId:', err))
        );

        if (previousSubscriptionId) {
          tasks.push(
            firstValueFrom(this.firestoreService.updateSubscription(previousSubscriptionId, {
              status: 'upgraded',
              upgradedTo: newSubscriptionId,
              upgradedAt: new Date()
            }))
              .catch((err) => console.error('Failed to mark previous subscription as upgraded:', err))
          );
        }

        Promise.all(tasks).finally(() => {
          this.router.navigate(['/checkout'], {
            state: {
              selectedPlanLevel: planLevel,
              subscriptionId: newSubscriptionId,
              existingSubscriptionId: newSubscriptionId,
              previousSubscriptionId,
              isUpgrade
            }
          });
        });
      },
      error: (err) => {
        console.error('Failed to create upgrade subscription record:', err);
        this.router.navigate(['/checkout'], {
          state: {
            selectedPlanLevel: planLevel,
            existingSubscriptionId: this.latestSubscriptionId,
            previousSubscriptionId: this.latestSubscriptionId,
            isUpgrade
          }
        });
      }
    });
  }

  enablePlanSelectorForInactive(): void {
    this.showPlanSelectorForInactive = true;
  }

  get shouldShowPlanCards(): boolean {
    return this.isSubscriptionActive || this.showPlanSelectorForInactive;
  }

  redirectToSubscriptions(){
    this.router.navigate(['/subscriptions']);
  }
}
